import { useEffect, useRef, useState } from 'react';
import { Menu as MuiMenu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button as MuiButton } from '@mui/material';
import {
  Search,
  Send,
  MoreVertical,
  Phone,
  Video,
  Smile,
  Paperclip,
  ArrowLeft,
  Users,
  MessageCircle,
  Plus,
  ImageIcon,
  Hash,
  Circle,
  ChevronLeft,
  ChevronRight,
  X,
  Pencil,
  Archive,
  Trash2,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import { useConversations, useMessages, useSendMessage, useChatSocket, useUpdateConversation, useMarkAsRead, useDeleteConversation } from '@/hooks/useChat';
import { getInitials } from '@/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';

dayjs.extend(relativeTime);
dayjs.locale('vi');

export default function ChatPage() {
  const user = useAuthStore((s) => s.user);
  const {
    conversations,
    activeConversationId,
    messages,
    setActiveConversation,
  } = useChatStore();

  const [searchConv, setSearchConv] = useState('');
  const [msgInput, setMsgInput] = useState('');
  const [mobileShowChat, setMobileShowChat] = useState(false);

  // Message search states
  const [showMsgSearch, setShowMsgSearch] = useState(false);
  const [searchMsgKeyword, setSearchMsgKeyword] = useState('');
  const [currentMatchIdx, setCurrentMatchIdx] = useState(0);

  const { isLoading: loadingConvs } = useConversations();
  const { isLoading: loadingMsgs } = useMessages(activeConversationId);
  const sendMessageMut = useSendMessage();
  const updateConvMut = useUpdateConversation();
  const markAsReadMut = useMarkAsRead();
  const deleteConvMut = useDeleteConversation();
  useChatSocket();

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuConvId, setMenuConvId] = useState<string | null>(null);

  // Dialog states
  const [editTitleOpen, setEditTitleOpen] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [targetConvId, setTargetConvId] = useState<string | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Reset search when switching conversations
  useEffect(() => {
    setShowMsgSearch(false);
    setSearchMsgKeyword('');
    setCurrentMatchIdx(0);
  }, [activeConversationId]);


  // Auto-scroll to bottom when new messages (only scroll the chat container, not the page)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, activeConversationId]);



  const handleSend = () => {
    const content = msgInput.trim();
    if (!content || !activeConversationId) return;
    sendMessageMut.mutate({
      conversationId: activeConversationId,
      content,
    });
    setMsgInput('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const selectConversation = (id: string) => {
    setActiveConversation(id);
    setMobileShowChat(true);
    
    // Auto mark as read if has unread
    const conv = conversations.find(c => c.id === id);
    if (conv && (conv.unreadCount ?? 0) > 0) {
      markAsReadMut.mutate(id);
    }
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuConvId(id);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuConvId(null);
  };

  const handleArchive = (id: string, currentStatus: boolean) => {
    updateConvMut.mutate({ conversationId: id, data: { isArchived: !currentStatus } });
    handleCloseMenu();
  };

  const handleDelete = () => {
    if (targetConvId) {
      deleteConvMut.mutate(targetConvId, {
        onSuccess: () => {
          if (activeConversationId === targetConvId) {
            setActiveConversation(null);
          }
          setDeleteConfirmOpen(false);
          setTargetConvId(null);
        }
      });
    }
  };

  const handleEditTitleSubmit = () => {
    if (targetConvId && editTitleValue.trim()) {
      updateConvMut.mutate({ conversationId: targetConvId, data: { title: editTitleValue.trim() } }, {
        onSuccess: () => {
          setEditTitleOpen(false);
          setTargetConvId(null);
        }
      });
    }
  };

  const activeConv = conversations.find((c) => c.id === activeConversationId);
  const activeMessages = activeConversationId ? messages[activeConversationId] || [] : [];

  // Compute matching messages for keyword search
  const matches = activeMessages
    .map((msg, idx) => ({ msg, idx }))
    .filter(({ msg }) => {
      if (!searchMsgKeyword.trim()) return false;
      return msg.content.toLowerCase().includes(searchMsgKeyword.toLowerCase());
    });

  const handlePrevMatch = () => {
    if (matches.length === 0) return;
    setCurrentMatchIdx((prev) => (prev - 1 + matches.length) % matches.length);
  };

  const handleNextMatch = () => {
    if (matches.length === 0) return;
    setCurrentMatchIdx((prev) => (prev + 1) % matches.length);
  };

  // Scroll to active matching message when search match index changes (exclusively in inner container)
  useEffect(() => {
    if (matches.length > 0 && showMsgSearch) {
      const activeMatch = matches[currentMatchIdx];
      if (activeMatch) {
        const container = messagesContainerRef.current;
        const el = document.getElementById(`msg-${activeMatch.msg.id}`);
        if (container && el) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();

          // Calculate target scrollTop to center element relative to container viewport
          const relativeTop = elRect.top - containerRect.top + container.scrollTop;
          const targetScrollTop = relativeTop - (containerRect.height / 2) + (elRect.height / 2);

          container.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [currentMatchIdx, searchMsgKeyword, showMsgSearch]);

  const getMessageDateLabel = (dateStr: string) => {
    const date = dayjs(dateStr);
    const today = dayjs().startOf('day');
    const yesterday = dayjs().subtract(1, 'day').startOf('day');
    const msgDay = date.startOf('day');

    if (msgDay.isSame(today, 'day')) {
      return 'Hôm nay';
    } else if (msgDay.isSame(yesterday, 'day')) {
      return 'Hôm qua';
    } else {
      return date.format('DD/MM/YYYY');
    }
  };

  const renderHighlightedContent = (content: string) => {
    if (!searchMsgKeyword.trim()) return content;
    const regex = new RegExp(`(${searchMsgKeyword.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
    const parts = content.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-amber-200 text-gray-900 rounded-sm px-0.5 font-bold animate-pulse">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getConvName = (conv: typeof conversations[0]) => {
    if (conv.title) return conv.title;
    const participants = conv.participants ?? [];
    const other = participants.filter((p) => p.id !== user?.id);
    return other.map((p) => p.fullName || `${p.firstName} ${p.lastName}`.trim() || p.username).join(', ') || 'Cuộc trò chuyện';
  };

  const getConvAvatar = (conv: typeof conversations[0]) => {
    const participants = conv.participants ?? [];
    const other = participants.find((p) => p.id !== user?.id);
    return other ? getInitials(other.fullName || `${other.firstName} ${other.lastName}` || other.username) : '?';
  };

  const filteredConvs = conversations.filter((c) => {
    if (!searchConv.trim()) return true;
    const name = getConvName(c).toLowerCase();
    return name.includes(searchConv.toLowerCase());
  });

  return (
    <div className="chat-page-container">
      {/* ═══ SIDEBAR: Danh sách hội thoại ═══ */}
      <aside className={cn('chat-sidebar', mobileShowChat && 'chat-sidebar--hidden-mobile')}>
        {/* Sidebar Header */}
        <div className="chat-sidebar__header">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <MessageCircle size={20} className="text-orange-500" />
              Tin nhắn
            </h2>
            <button className="w-8 h-8 rounded-xl bg-orange-50 hover:bg-orange-100 flex items-center justify-center text-orange-500 transition-colors">
              <Plus size={16} />
            </button>
          </div>
          {/* Search */}
          <div className="chat-search">
            <Search size={15} className="text-gray-400 shrink-0" />
            <input
              value={searchConv}
              onChange={(e) => setSearchConv(e.target.value)}
              placeholder="Tìm cuộc trò chuyện..."
              className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="chat-sidebar__list">
          {loadingConvs ? (
            <div className="p-6 text-center">
              <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto" />
              <p className="text-xs text-gray-400 mt-3">Đang tải...</p>
            </div>
          ) : filteredConvs.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-3">
                <MessageCircle size={28} className="text-orange-300" />
              </div>
              <p className="text-sm font-semibold text-gray-500">Chưa có cuộc trò chuyện</p>
              <p className="text-xs text-gray-400 mt-1">Bắt đầu nhắn tin ngay!</p>
            </div>
          ) : (
            filteredConvs.map((conv) => {
              const isActive = conv.id === activeConversationId;
              const hasUnread = (conv.unreadCount ?? 0) > 0;
              return (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv.id)}
                  className={cn(
                    'chat-conv-item',
                    isActive && 'chat-conv-item--active',
                  )}
                >
                  {/* Avatar */}
                  <div className="chat-conv-avatar">
                    <span className="text-sm font-bold text-white">{getConvAvatar(conv)}</span>
                    {/* Online indicator */}
                    <div className="chat-conv-avatar__online" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={cn('text-sm truncate', hasUnread ? 'font-bold text-gray-800' : 'font-semibold text-gray-700')}>
                        {getConvName(conv)}
                      </p>
                      {conv.lastMessage && (
                        <span className="text-[10px] text-gray-400 shrink-0 ml-2 font-medium">
                          {dayjs(conv.lastMessage.createdAt).fromNow()}
                        </span>
                      )}
                    </div>
                    {conv.lastMessage && (
                      <p className={cn('text-xs truncate mt-0.5', hasUnread ? 'text-gray-600 font-medium' : 'text-gray-400')}>
                        {conv.lastMessage.senderId === user?.id ? 'Bạn: ' : ''}
                        {conv.lastMessage.content}
                      </p>
                    )}
                  </div>

                  {/* Unread Badge & More Actions */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {hasUnread && (
                      <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold text-white bg-orange-500 shrink-0">
                        {conv.unreadCount}
                      </span>
                    )}
                    <button
                      onClick={(e) => handleOpenMenu(e, conv.id)}
                      className="w-6 h-6 rounded-md hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                    >
                      <MoreVertical size={14} />
                    </button>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* --- Action Menu --- */}
      <MuiMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        sx={{ '& .MuiPaper-root': { borderRadius: '12px', minWidth: '160px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            const conv = conversations.find(c => c.id === menuConvId);
            setEditTitleValue(conv?.title || '');
            setTargetConvId(menuConvId);
            setEditTitleOpen(true);
            handleCloseMenu();
          }}
          sx={{ fontSize: '13px', py: 1.5, gap: 1.5 }}
        >
          <Pencil size={16} className="text-gray-500" /> Đổi tên
        </MenuItem>
        
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            if (menuConvId) markAsReadMut.mutate(menuConvId);
            handleCloseMenu();
          }}
          sx={{ fontSize: '13px', py: 1.5, gap: 1.5 }}
        >
          <CheckCircle2 size={16} className="text-green-500" /> Đánh dấu đã đọc
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            const conv = conversations.find(c => c.id === menuConvId);
            if (menuConvId) handleArchive(menuConvId, (conv as any)?.isArchived || false);
          }}
          sx={{ fontSize: '13px', py: 1.5, gap: 1.5 }}
        >
          <Archive size={16} className="text-orange-500" /> {(conversations.find(c => c.id === menuConvId) as any)?.isArchived ? 'Bỏ lưu trữ' : 'Lưu trữ'}
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setTargetConvId(menuConvId);
            setDeleteConfirmOpen(true);
            handleCloseMenu();
          }}
          sx={{ fontSize: '13px', py: 1.5, gap: 1.5, color: '#ef4444' }}
        >
          <Trash2 size={16} /> Xóa cuộc trò chuyện
        </MenuItem>
      </MuiMenu>

      {/* --- Edit Title Dialog --- */}
      <Dialog open={editTitleOpen} onClose={() => setEditTitleOpen(false)} sx={{ '& .MuiDialog-paper': { borderRadius: '16px', width: '500px', maxWidth: 'none', p: 1 } }}>
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 'bold' }}>Đổi tên cuộc trò chuyện</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Tên mới"
            type="text"
            fullWidth
            variant="outlined"
            value={editTitleValue}
            onChange={(e) => setEditTitleValue(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '& fieldset': {
                  borderColor: '#f97316',
                  borderWidth: '1.5px',
                },
                '&:hover fieldset': {
                  borderColor: '#ea580c',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#ea580c',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#f97316',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ea580c',
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => setEditTitleOpen(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 'bold' }}>Hủy</MuiButton>
          <MuiButton onClick={handleEditTitleSubmit} disabled={!editTitleValue.trim()} sx={{ color: '#f97316', textTransform: 'none', fontWeight: 'bold' }}>Lưu thay đổi</MuiButton>
        </DialogActions>
      </Dialog>

      {/* --- Delete Confirm Dialog --- */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} sx={{ '& .MuiDialog-paper': { borderRadius: '16px', width: '400px', maxWidth: 'none' } }}>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 'bold', color: '#ef4444' }}>Xóa cuộc trò chuyện</DialogTitle>
        <DialogContent>
          Hành động này không thể hoàn tác. Bạn có chắc chắn muốn xóa cuộc trò chuyện này không?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => setDeleteConfirmOpen(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 'bold' }}>Hủy</MuiButton>
          <MuiButton onClick={handleDelete} sx={{ color: '#ef4444', textTransform: 'none', fontWeight: 'bold' }}>Xóa vĩnh viễn</MuiButton>
        </DialogActions>
      </Dialog>

      {/* ═══ MAIN CHAT AREA ═══ */}
      <main className={cn('chat-main', !mobileShowChat && 'chat-main--hidden-mobile')}>
        {!activeConversationId ? (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center mb-5">
              <MessageCircle size={42} className="text-orange-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">Chào mừng đến FnBHub Chat</h3>
            <p className="text-sm text-gray-400 max-w-xs">
              Chọn một cuộc trò chuyện để bắt đầu nhắn tin, hoặc tạo cuộc trò chuyện mới.
            </p>
            <div className="mt-6 flex items-center gap-3 text-xs text-gray-400">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50">
                <Circle size={8} className="fill-green-400 text-green-400" />
                Online
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50">
                🔒 Mã hóa E2E
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* ── Chat Header ── */}
            <div className="chat-main__header">
              <div className="flex items-center gap-3">
                {/* Back button (mobile) */}
                <button
                  onClick={() => {
                    setMobileShowChat(false);
                    setActiveConversation(null);
                  }}
                  className="md:hidden w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500"
                >
                  <ArrowLeft size={18} />
                </button>

                <div className="chat-conv-avatar chat-conv-avatar--sm">
                  <span className="text-xs font-bold text-white">{activeConv ? getConvAvatar(activeConv) : '?'}</span>
                  <div className="chat-conv-avatar__online chat-conv-avatar__online--sm" />
                </div>

                <div>
                  <p className="text-sm font-bold text-gray-800">{activeConv ? getConvName(activeConv) : ''}</p>
                  <p className="text-[11px] text-green-500 font-medium flex items-center gap-1">
                    <Circle size={6} className="fill-green-500" />
                    Đang hoạt động
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowMsgSearch((s) => !s)}
                  className={cn(
                    "w-8 h-8 rounded-lg hover:bg-orange-50 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-all duration-300",
                    showMsgSearch && "bg-orange-100 text-orange-600 font-bold"
                  )}
                  title="Tìm kiếm tin nhắn"
                >
                  <Search size={17} />
                </button>
              </div>
            </div>

            {/* ── Message Search Bar ── */}
            {showMsgSearch && (
              <div className="px-4 py-2 border-b bg-gray-50/80 backdrop-blur flex items-center justify-between gap-3 animate-in slide-in-from-top duration-300">
                <div className="flex-1 flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5 shadow-sm">
                  <Search size={14} className="text-gray-400 shrink-0" />
                  <input
                    type="text"
                    value={searchMsgKeyword}
                    onChange={(e) => {
                      setSearchMsgKeyword(e.target.value);
                      setCurrentMatchIdx(0);
                    }}
                    placeholder="Tìm kiếm nội dung tin nhắn..."
                    className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder:text-gray-400"
                    autoFocus
                  />
                  {searchMsgKeyword && (
                    <button
                      onClick={() => {
                        setSearchMsgKeyword('');
                        setCurrentMatchIdx(0);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {/* Search Navigation */}
                {matches.length > 0 && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] text-gray-500 font-bold bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
                      {currentMatchIdx + 1} / {matches.length}
                    </span>
                    <button
                      onClick={handlePrevMatch}
                      className="p-1 rounded-lg hover:bg-gray-200 text-gray-600 transition-colors"
                      title="Kết quả trước"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={handleNextMatch}
                      className="p-1 rounded-lg hover:bg-gray-200 text-gray-600 transition-colors"
                      title="Kết quả sau"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                )}
                {searchMsgKeyword && matches.length === 0 && (
                  <span className="text-[11px] text-red-500 font-semibold shrink-0">
                    Không tìm thấy kết quả
                  </span>
                )}
                <button
                  onClick={() => {
                    setShowMsgSearch(false);
                    setSearchMsgKeyword('');
                  }}
                  className="text-xs font-bold text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 transition-all shrink-0"
                >
                  Đóng
                </button>
              </div>
            )}

            {/* ── Messages ── */}
            <div className="chat-messages" ref={messagesContainerRef}>
              {loadingMsgs ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
                </div>
              ) : activeMessages.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                  <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-3">
                    <Send size={24} className="text-orange-300" />
                  </div>
                  <p className="text-sm font-semibold text-gray-500">Bắt đầu cuộc trò chuyện</p>
                  <p className="text-xs text-gray-400 mt-1">Hãy gửi lời chào đầu tiên! 🎉</p>
                </div>
              ) : (
                <>
                  {activeMessages.map((msg, idx) => {
                    const isMine = msg.senderId === user?.id;
                    const showAvatar = !isMine && (idx === 0 || activeMessages[idx - 1]?.senderId !== msg.senderId);
                    const senderName = msg.sender
                      ? msg.sender.fullName || `${msg.sender.firstName} ${msg.sender.lastName}`.trim()
                      : '';

                    const isMatched = !!searchMsgKeyword.trim() && msg.content.toLowerCase().includes(searchMsgKeyword.toLowerCase());
                    const isActiveMatch = matches[currentMatchIdx]?.msg.id === msg.id;

                    // Calculate dynamic date separators
                    const currentDateLabel = getMessageDateLabel(msg.createdAt);
                    const prevDateLabel = idx > 0 ? getMessageDateLabel(activeMessages[idx - 1].createdAt) : null;
                    const showDateSeparator = idx === 0 || currentDateLabel !== prevDateLabel;

                    return (
                      <div key={msg.id} className="flex flex-col">
                        {showDateSeparator && (
                          <div className="flex items-center gap-3 py-4 select-none">
                            <div className="flex-1 h-px bg-gray-100" />
                            <span className="text-[10px] text-gray-400 font-bold bg-gray-50 border border-gray-100/60 px-3 py-1 rounded-full">
                              {currentDateLabel}
                            </span>
                            <div className="flex-1 h-px bg-gray-100" />
                          </div>
                        )}

                        <div
                          id={`msg-${msg.id}`}
                          className={cn('chat-msg transition-all duration-300', isMine ? 'chat-msg--mine' : 'chat-msg--other')}
                        >
                          {/* Avatar for other */}
                          {!isMine && (
                            <div className={cn('chat-msg__avatar', !showAvatar && 'invisible')}>
                              <span className="text-[10px] font-bold text-white">
                                {senderName ? getInitials(senderName) : '?'}
                              </span>
                            </div>
                          )}

                          <div
                            className={cn(
                              'chat-msg__bubble transition-all duration-300',
                              isMine ? 'chat-msg__bubble--mine' : 'chat-msg__bubble--other',
                              isMatched && 'border border-amber-400/60 shadow-sm shadow-amber-200/50',
                              isActiveMatch && 'ring-2 ring-orange-500 ring-offset-2 scale-[1.02] shadow-lg'
                            )}
                          >
                            {/* Sender name for group */}
                            {!isMine && showAvatar && activeConv?.isGroup && (
                              <p className="text-[10px] font-bold text-orange-500 mb-1">{senderName}</p>
                            )}
                            <p className="text-[13px] leading-relaxed whitespace-pre-wrap break-words">
                              {renderHighlightedContent(msg.content)}
                            </p>
                            <p className={cn('text-[9px] mt-1', isMine ? 'text-orange-200' : 'text-gray-400')}>
                              {dayjs(msg.createdAt).format('HH:mm')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                </>
              )}
            </div>

            {/* ── Input Area ── */}
            <div className="chat-input-area">
              {/* <div className="chat-input-toolbar">
                <button className="w-8 h-8 rounded-lg hover:bg-orange-50 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors">
                  <Smile size={18} />
                </button>
                <button className="w-8 h-8 rounded-lg hover:bg-orange-50 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors">
                  <Paperclip size={18} />
                </button>
                <button className="w-8 h-8 rounded-lg hover:bg-orange-50 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors">
                  <ImageIcon size={18} />
                </button>
              </div> */}

              <div className="chat-input-row">
                <textarea
                  ref={inputRef}
                  value={msgInput}
                  onChange={(e) => setMsgInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Nhập tin nhắn..."
                  rows={1}
                  className="chat-input-textarea"
                />
                <button
                  onClick={handleSend}
                  disabled={!msgInput.trim() || sendMessageMut.isPending}
                  className={cn(
                    'chat-send-btn',
                    msgInput.trim() ? 'chat-send-btn--active' : 'chat-send-btn--disabled',
                  )}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
